import type { ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useDialog } from './useDialog'

export interface ModalProps {
  children: ReactNode
  onClose: () => void
  /** Id of the modal's title (`aria-labelledby`). */
  labelledBy: string
  /** Id of a description (`aria-describedby`). */
  describedBy?: string
  wide?: boolean
  /** false: Escape and the backdrop don't close it (C-13b, the leave prompt). Default true. */
  dismissible?: boolean
  /** Where focus goes when it opens. Default: the first tabbable element. */
  initialFocus?: RefObject<HTMLElement | null>
}

/** Centred dialog (mockup `ui.tsx` → `Modal`) with a focus trap, initial focus and return focus. */
export function Modal({
  children,
  onClose,
  labelledBy,
  describedBy,
  wide,
  dismissible = true,
  initialFocus,
}: ModalProps) {
  const { dialogRef, onKeyDown, onBackdropMouseDown } = useDialog({
    onClose,
    dismissible,
    ...(initialFocus && { initialFocus }),
  })
  return createPortal(
    // The backdrop click is a mouse shortcut; Escape is the keyboard equivalent (handled in onKeyDown).
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="overlay" onMouseDown={onBackdropMouseDown} onKeyDown={onKeyDown}>
      <div
        ref={(el) => {
          dialogRef.current = el
        }}
        className={`modal ${wide ? 'wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
